/*!
 * Copyright (c) Microsoft Corporation.
 * All rights reserved. See LICENSE in the project root for license information.
 */

import * as React from 'react';

import { getBackOrderListAsync } from '../../actions/DataActionExtension.g';
import { IBackOrderListEntity, IGetBackOrderListResponse } from '../../actions/DataServiceEntities.g';
import { IBackOrderListData } from './back-order-list.data';
import { IBackOrderListProps } from './back-order-list.props.autogenerated';
import { QueryResultSettingsProxy } from '@msdyn365-commerce-modules/retail-actions';
// import getBackOrderList, { GetBackorderListInput } from '../../actions/get-backorder-list.action';
import Pagination from '../../components/Pagination';
import { BackOrderTableComponent, IBackOrderTableComponentResources } from './components/BackOrderTable.component';
import { reaction } from 'mobx';
import { observer } from 'mobx-react';

/**
 * Props for the view component
 */
export interface IBackOrderListViewProps extends IBackOrderListProps<IBackOrderListData> {
    modelTable: React.ReactNode;
    pagination: React.ReactNode;
    // selection: React.ReactNode;
}

/**
 * Component state
 */
interface IBackOrderListState {
    page: number;
    backOrders: IBackOrderListEntity[];
    totalCount: number;
    availableSortOptions: string[];
    sortOrder: string;
    selectedSort: string;
    loading: boolean;
}

/**
 *
 * BackOrderList component
 * @extends {React.PureComponent<IBackOrderListProps<IBackOrderListData>>}
 */
@observer
class BackOrderList extends React.PureComponent<IBackOrderListProps<IBackOrderListData>, IBackOrderListState> {
    private static defaultBackOrderItemsPerPage = 15;

    private readonly numItemsToDisplay = this.props.config.backOrderItemsPerPage || BackOrderList.defaultBackOrderItemsPerPage;

    constructor(props: IBackOrderListProps<IBackOrderListData>) {
        super(props);

        const queryResultSettings = QueryResultSettingsProxy.fromInputData(props.context.actionContext).QueryResultSettings;
        let page = 1;
        if (queryResultSettings.Paging?.Skip && queryResultSettings.Paging.Skip > 0) {
            page = this.numItemsToDisplay / queryResultSettings.Paging.Skip;
        }
        this.state = {
            page,
            backOrders: this.props.data.backOrderResults.result?.backOrderData.BackOrderLines || [],
            totalCount: this.props.data.backOrderResults.result?.backOrderData.RecordCount || 0,
            availableSortOptions: this.props.data.backOrderResults.result?.backOrderData.AvailableSortOptions || [],
            sortOrder: this.props.data.backOrderResults.result?.backOrderData.SortOrder || '',
            selectedSort: this.props.data.backOrderResults.result?.backOrderData.SelectedSort || '',
            loading: true
        };

        this.handlePaginationPageChange = this.handlePaginationPageChange.bind(this);
        this.handleSortingTypeChange = this.handleSortingTypeChange.bind(this);
        this.setBackOrderListResults = this.setBackOrderListResults.bind(this);
        this.updateBackOrderResults = this.updateBackOrderResults.bind(this);
        this.toggleSortOrder = this.toggleSortOrder.bind(this);
    }

    public componentDidMount(): void {
        reaction(
            () => this.props.data.backOrderResults.result?.backOrderData,
            () => this.setBackOrderListResults(this.props.data.backOrderResults.result!.backOrderData)
        );
    }

    private setBackOrderListResults(backOrderListResponse: IGetBackOrderListResponse) {
        this.setState({
            backOrders: backOrderListResponse.BackOrderLines || [],
            totalCount: backOrderListResponse.RecordCount || 0,
            availableSortOptions: backOrderListResponse.AvailableSortOptions || [],
            sortOrder: backOrderListResponse.SortOrder || '',
            selectedSort: backOrderListResponse.SelectedSort || '',
            loading: false
        });
    }

    /**
     * Updates the results of the model when paginating
     * @private
     */
    private async updateBackOrderResults() {
        const top = this.numItemsToDisplay;
        const skip = this.state.page * top - top;
        const selectedSort = this.state.selectedSort;
        const sortOrder = this.state.sortOrder;
        const {
            context: { actionContext }
        } = this.props;

        // const getBackorderListInput = new GetBackorderListInput(request, request.apiSettings, top, skip, selectedSort, sortOrder);
        // console.log(getBackorderListInput);
        // const res = await getBackOrderList(getBackorderListInput, actionContext);
        if (this.props.config.splitGetBackordersCall && top > 3) {
            // split getBackOrderList call into 3
            let totalItems = top;
            const numSplits = totalItems >= 15 ? 4 : 3;
            const numPerSplit = Math.ceil(totalItems / numSplits);
            const numPerSplitArray: number[] = [];
            const aggResponse: IGetBackOrderListResponse = {
                BackOrderLines: [],
                RecordCount: 0,
                AvailableSortOptions: [],
                SortOrder: '',
                SelectedSort: ''
            };
            while (totalItems > numPerSplit) {
                numPerSplitArray.push(numPerSplit);
                totalItems -= numPerSplit;
            }
            if (totalItems > 0) {
                numPerSplitArray.push(totalItems);
            }
            const results = await Promise.all(
                numPerSplitArray.map((val, idx) => {
                    return getBackOrderListAsync(
                        { callerContext: actionContext },
                        val,
                        skip + numPerSplitArray.slice(0, idx).reduce((partialSum, a) => partialSum + a, 0),
                        selectedSort,
                        sortOrder
                    );
                })
            );
            results.forEach(resp => {
                resp.BackOrderLines && aggResponse.BackOrderLines?.push(...resp.BackOrderLines);
                resp.RecordCount && (aggResponse.RecordCount = resp.RecordCount);
                resp.AvailableSortOptions && (aggResponse.AvailableSortOptions = resp.AvailableSortOptions);
                resp.SortOrder && (aggResponse.SortOrder = resp.SortOrder);
                resp.SelectedSort && (aggResponse.SelectedSort = resp.SelectedSort);
                if (resp.Top !== undefined && aggResponse.Top !== undefined && resp.Top >= 0 && aggResponse.Top >= 0) {
                    aggResponse.Top += resp.Top;
                }
            });
            aggResponse.Skip = skip;
            void this.setBackOrderListResults(aggResponse);
        } else {
            const res = await getBackOrderListAsync({ callerContext: actionContext }, top, skip, selectedSort, sortOrder);
            void this.setBackOrderListResults(res);
        }
        // void getBackOrderList(getBackorderListInput, actionContext).then(res => {
        //     console.log(res);
        //     this.setBackOrderListResults(res.backOrderData);
        //     // this.setState({
        //     //     backOrders: res.backOrderData.BackOrderLines || [],
        //     //     totalCount: res.backOrderData.RecordCount || 0,
        //     //     sortOrder: res.backOrderData.SortOrder || '',
        //     //     selectedSort: res.backOrderData.SelectedSort || ''
        //     // });
        // });
    }

    /**
     * Renders the model table
     * @param modelsList model list for the table
     * @param totalCount total count of models for pagination
     * @private
     */
    private getModelTable(backOrders: IBackOrderListEntity[], totalCount: number) {
        const {
            backOrderListTitleText,
            backOrderListItemsText,
            orderNumberHeadingText,
            poNumberHeadingText,
            mfrHeadingText,
            partNumberHeadingText,
            deliveryTypeHeadingText,
            qtyHeadingText,
            totalHeadingText,
            noBackOrdersMessageText,
            loadingMessageText,
            estShipDateHeadingText
        } = this.props.resources;
        const backOrdersListResources: IBackOrderTableComponentResources = {
            backOrderListTitleText,
            backOrderListItemsText,
            orderNumberHeadingText,
            poNumberHeadingText,
            mfrHeadingText,
            partNumberHeadingText,
            deliveryTypeHeadingText,
            qtyHeadingText,
            totalHeadingText,
            noBackOrdersMessageText,
            estShipDateHeadingText,
            loadingMessageText
        };

        return (
            <BackOrderTableComponent
                backOrders={backOrders}
                resources={backOrdersListResources}
                totalCount={totalCount}
                backOrderEtaBuffer={this.props.config.backOrderEtaBuffer || 0}
                ctx={this.props.context}
                sortingDropdown={this._renderSortingType()}
                sortOrderButton={this.renderSortOrderButton()}
                currencyFormatter={this.props.context.cultureFormatter.formatCurrency}
                config={this.props.config}
                loading={this.state.loading}
            />
        );
    }

    /**
     * Handles user pagination
     * @param pageNumber page to change to
     * @private
     */
    private handlePaginationPageChange(pageNumber: number) {
        this.setState(
            {
                page: pageNumber
            },
            () => this.updateBackOrderResults()
        );
    }

    private generateUrlForPagination() {
        const url = this.props.context.request.url;
        return url.requestUrl.toString();
    }

    /**
     * renders the pagination
     * @private
     */
    private renderPagination() {
        const {
            resources: { previousText, nextText, paginationAriaLabel }
        } = this.props;

        if (this.state.totalCount === 0) {
            return '';
        }
        return (
            <Pagination
                className='ms-search-result-container__pagination'
                role='navigation'
                aria-label={paginationAriaLabel}
                qsp='skip'
                items={this.state.totalCount}
                itemsPerPage={this.numItemsToDisplay}
                startingItem={this.state.page}
                previousText={this._renderPrev(previousText)}
                nextText={this._renderNext(nextText)}
                previousAriaLabel={previousText}
                nextAriaLabel={nextText}
                url={this.generateUrlForPagination()}
                onPageChange={this.handlePaginationPageChange}
                page={this.state.page}
            />
        );
    }

    /**
     * Renders a Pagination previous text.
     * @param  paginationPreviousText - Pagination previous text from resources.
     * @returns Previous text and flipper icon.
     */
    private readonly _renderPrev = (paginationPreviousText: string) => {
        return (
            <div className='msc-pagination__prev'>
                <span className='ms-search-result__pagination-left' aria-hidden='true' />
                <span className='prev-text'>{paginationPreviousText}</span>
            </div>
        );
    };

    /**
     * Renders a Pagination next text.
     * @param  paginationNextText - Pagination next text from resources.
     * @returns Next text and flipper icon.
     */
    private readonly _renderNext = (paginationNextText: string) => {
        return (
            <div className='msc-pagination__next'>
                <span className='next-text'>{paginationNextText}</span>
                <span className='ms-search-result__pagination-right' aria-hidden='true' />
            </div>
        );
    };

    private toggleSortOrder(sortOrder: string): void {
        this.setState(
            {
                sortOrder: sortOrder === 'Ascending' ? 'Descending' : 'Ascending',
                page: 1
            },
            () => this.updateBackOrderResults()
        );
    }

    private renderSortOrderButton() {
        // $msv-ChevronDown: "\ea4e";
        // $msv-ChevronUp: "\ea78";
        const sortOrder = this.state.sortOrder;
        return (
            <button className='msc-btn sort-order-button' onClick={() => this.toggleSortOrder(sortOrder)}>
                {sortOrder === 'Ascending' ? '↑' : '↓'}
            </button>
        );
    }

    /**
     * Handles sort by dropdown changes
     * @param pageNumber page to change to
     * @private
     */
    private async handleSortingTypeChange(e: React.ChangeEvent<HTMLSelectElement>) {
        this.setState(
            {
                selectedSort: e.target.value,
                page: 1
            },
            () => this.updateBackOrderResults()
        );
    }

    private readonly _renderSortingType = () => {
        return (
            <div className='back-order-results-dropdown'>
                <p className='back-order-results-dropdown--label'>{this.props.resources.sortingSelectLabelText}</p>
                <select
                    name='sorting-type-select'
                    id='sorting-type-select'
                    className={`back-order-results-dropdown--sorting-type`}
                    value={this.state.selectedSort}
                    onChange={this.handleSortingTypeChange}
                >
                    {!this.state.selectedSort && <option value={''}>{this.props.resources.defaultSortingSelectOption}</option>}
                    {this.state.availableSortOptions &&
                        this.state.availableSortOptions.length > 0 &&
                        this.state.availableSortOptions.map(sortOptions => (
                            <option value={sortOptions} key={sortOptions}>
                                {sortOptions}
                            </option>
                        ))}
                </select>
            </div>
        );
    };

    public render(): JSX.Element | null {
        const { backOrders, totalCount } = this.state;

        const viewProps: IBackOrderListViewProps = {
            ...this.props,
            // selection: this._renderSortingType(),
            modelTable: this.getModelTable(backOrders, totalCount),
            pagination: this.renderPagination()
        };
        return this.props.renderView(viewProps);
    }
}

export default BackOrderList;
